"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Edit3,
  Mail,
  MessageSquare,
  Search,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/utils/customToast.tsx";
import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS } from "@/constants/api.ts";
import { searchStudents, type StudentSearchResult } from "@/features/workspaces/api/searchStudents";
import {
  createInvitation,
  type CreateInvitationRequest,
} from "@/features/workspaces/api/createInvitation";

// Simple email validation function
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(email);
};

const getLinkInviteSchema = (t: any) =>
  z.object({
    description: z.string().max(500, t("forms:validation.description_max_500")).optional(),
    type: z.enum(["ONE_TIME", "UNLIMITED"]),
  });

const getEmailInviteSchema = (t: any) =>
  z.object({
    email: z.string().optional(), // Email is now optional since we're validating user_ids
    description: z.string().max(500, t("forms:validation.description_max_500")).optional(),
  });

type LinkInviteFormValues = z.infer<ReturnType<typeof getLinkInviteSchema>>;
type EmailInviteFormValues = z.infer<ReturnType<typeof getEmailInviteSchema>>;

interface InviteFormProps {
  onComplete: () => void;
  onBack: () => void;
  selectedType: "link" | "email";
}

export function InviteForm({ onComplete, onBack, selectedType }: InviteFormProps) {
  const { t } = useTranslation(["onboarding", "workspaces", "common"]);
  const [submitting, setSubmitting] = useState(false);
  const [invitationLink, setInvitationLink] = useState<string | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<StudentSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  const linkForm = useForm<LinkInviteFormValues>({
    resolver: zodResolver(getLinkInviteSchema(t)),
    defaultValues: {
      description: "",
      type: "ONE_TIME",
    },
  });

  const emailForm = useForm<EmailInviteFormValues>({
    resolver: zodResolver(getEmailInviteSchema(t)),
    defaultValues: {
      email: "",
      description: "",
    },
  });

  const handleLinkInviteSubmit = async (data: LinkInviteFormValues) => {
    try {
      setSubmitting(true);
      const response = await axiosApiInstance.post(
        `${API_ENDPOINTS.WORKSPACES.INVITATIONS.CREATE}`,
        null,
        {
          params: {
            description: data.description,
            type: data.type,
          },
        },
      );

      const invitationId = response.data.data.invitation_id;
      const link = `${window.location.origin}/invite/${invitationId}`;

      setInvitationLink(link);

      toast.success(t("workspaces:invitationCreated", "Invitation link created successfully!"));
    } catch (error) {
      console.error(error);
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Call onStudentsChange when selectedStudents changes
  useEffect(() => {
    // This could be used to update parent component if needed
  }, [selectedStudents]);

  const handleSearch = useCallback(
    async (email: string) => {
      if (!email || !isValidEmail(email)) {
        setSearchError(null);

        return;
      }

      setSearching(true);
      setSearchError(null);

      try {
        const response = await searchStudents(email);

        if (response.data.length > 0) {
          const student = response.data[0];

          // Check if student is already in the list
          if (!selectedStudents.find((s) => s.id === student.id)) {
            setSelectedStudents((prev) => [...prev, student]);
            emailForm.setValue("email", ""); // Clear the input after successful search
            setValidationError(null); // Clear validation error when students are added
          } else {
            setSearchError(
              t("workspaces:studentAlreadyAdded", "Student already added to the list"),
            );
          }
        } else {
          setSearchError(t("workspaces:studentNotFound"));
        }
      } catch (error) {
        setSearchError(t("workspaces:searchError"));
      } finally {
        setSearching(false);
      }
    },
    [t, selectedStudents, emailForm],
  );

  const handleEmailBlur = useCallback(() => {
    const email = emailForm.getValues("email");

    if (email) {
      handleSearch(email);
    }
  }, [emailForm, handleSearch]);

  const handleSearchClick = useCallback(() => {
    const email = emailForm.getValues("email");

    if (email) {
      handleSearch(email);
    }
  }, [emailForm, handleSearch]);

  const handleEmailInviteSubmit = async (data: EmailInviteFormValues) => {
    // Validate that we have at least one student selected
    if (selectedStudents.length === 0) {
      setValidationError(
        t("workspaces:atLeastOneStudent", "At least one student must be selected"),
      );

      return;
    }

    setValidationError(null);
    setSubmitting(true);

    try {
      const submitData: CreateInvitationRequest = {
        type: "EMAIL",
        description: data.description,
        user_ids: selectedStudents.map((s) => s.id),
      };

      await createInvitation(submitData);
      setEmailSent(true);
      toast.success(t("workspaces:emailInviteSent", "Email invitations sent successfully!"));
    } catch (error) {
      console.error(error);
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const removeStudentFromSelected = (studentId: string) => {
    setSelectedStudents(selectedStudents.filter((s) => s.id !== studentId));
    setValidationError(null); // Clear validation error when students are removed
  };

  const canSubmit = selectedStudents.length > 0 && !submitting;

  const copyInvitationLink = () => {
    if (invitationLink) {
      navigator.clipboard.writeText(invitationLink);
      toast.success(t("common:copied", "Copied to clipboard!"));
    }
  };

  const handleComplete = () => {
    onComplete();
  };

  // Success view for link invitation
  if (invitationLink) {
    return (
      <div className="relative flex h-full w-full items-center justify-center">
        {/* Background circles */}
        <motion.div
          layout
          animate={{ opacity: 0.2 }}
          className="absolute -bottom-50 -left-50 size-120 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 opacity-30 transition-colors duration-700"
          initial={{ opacity: 0 }}
          layoutId="circle1"
          transition={{
            default: { duration: 0.75 },
            layout: { duration: 0.75 },
          }}
        />
        <motion.div
          layout
          animate={{ opacity: 0.2 }}
          className="absolute -top-50 -right-50 size-120 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 opacity-30 transition-colors duration-700"
          initial={{ opacity: 0.2 }}
          layoutId="circle2"
          transition={{
            layout: { duration: 0.75 },
          }}
        />

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-2xl space-y-6 text-center"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.4 }}
        >
          <motion.div
            animate={{ opacity: 1, scale: 1 }}
            initial={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                boxShadow: [
                  "0 0 0 0 rgba(34, 197, 94, 0.4)",
                  "0 0 0 10px rgba(34, 197, 94, 0)",
                  "0 0 0 0 rgba(34, 197, 94, 0)",
                ],
              }}
              className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20 sm:h-16 sm:w-16"
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <CheckCircle2 className="h-6 w-6 text-green-500 sm:h-8 sm:w-8" />
            </motion.div>
            <h2 className="text-accent-foreground mb-4 text-2xl font-bold sm:text-3xl lg:text-4xl">
              {t("onboarding:invite.success.title", "Invitation Link Created!")}
            </h2>
            <p className="text-muted-foreground mb-6 text-base sm:text-lg md:text-xl">
              {t(
                "onboarding:invite.success.description",
                "Share this link with your students so they can join your workspace.",
              )}
            </p>
          </motion.div>

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="bg-foreground/30 relative overflow-hidden rounded-xl p-4 backdrop-blur-sm"
            initial={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            {/* Decorative elements */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
              <motion.div
                animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                className="absolute -top-4 -left-4 h-8 w-8 rounded-full bg-green-500/20"
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
                className="absolute -right-4 -bottom-4 h-6 w-6 rounded-full bg-emerald-500/20"
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              />
            </div>
            <div className="relative z-10 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="bg-muted/50 flex-1 rounded-lg p-3 text-left">
                <code className="text-accent-foreground text-sm break-all">{invitationLink}</code>
              </div>
              <Button
                className="h-full w-full sm:w-auto"
                variant="foreground"
                onClick={copyInvitationLink}
              >
                <Icon className="mr-2 h-4 w-4" icon="tabler:copy" />
                {t("common:copy", "Copy")}
              </Button>
            </div>
          </motion.div>

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-3 sm:flex-row"
            initial={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Button
              className="w-full px-6 sm:w-auto sm:flex-1 sm:px-8"
              size="lg"
              variant="foreground"
              onClick={handleComplete}
            >
              {t("onboarding:invite.continue", "Continue to Dashboard")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // Success view for email invitation
  if (emailSent) {
    return (
      <div className="relative flex h-full w-full items-center justify-center">
        {/* Background circles */}
        <motion.div
          layout
          animate={{ opacity: 0.2 }}
          className="absolute -bottom-50 -left-50 size-120 rounded-full bg-gradient-to-br from-purple-500/20 to-violet-500/20 opacity-30 transition-colors duration-700"
          initial={{ opacity: 0 }}
          layoutId="circle1"
          transition={{
            default: { duration: 0.75 },
            layout: { duration: 0.75 },
          }}
        />
        <motion.div
          layout
          animate={{ opacity: 0.2 }}
          className="absolute -top-50 -right-50 size-120 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 opacity-30 transition-colors duration-700"
          initial={{ opacity: 0.2 }}
          layoutId="circle2"
          transition={{
            layout: { duration: 0.75 },
          }}
        />

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-2xl space-y-6 text-center"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.4 }}
        >
          <motion.div
            animate={{ opacity: 1, scale: 1 }}
            initial={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                boxShadow: [
                  "0 0 0 0 rgba(34, 197, 94, 0.4)",
                  "0 0 0 10px rgba(34, 197, 94, 0)",
                  "0 0 0 0 rgba(34, 197, 94, 0)",
                ],
              }}
              className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20 sm:h-16 sm:w-16"
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <CheckCircle2 className="h-6 w-6 text-green-500 sm:h-8 sm:w-8" />
            </motion.div>
            <h2 className="text-accent-foreground mb-4 text-2xl font-bold sm:text-3xl lg:text-4xl">
              {t("onboarding:invite.emailSuccess.title", "Email Invitations Sent!")}
            </h2>
            <p className="text-muted-foreground mb-6 text-base sm:text-lg md:text-xl">
              {t(
                "onboarding:invite.emailSuccess.description",
                "Your students will receive an email invitation to join your workspace.",
              )}
            </p>
          </motion.div>

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="bg-foreground/30 relative overflow-hidden rounded-xl p-4 backdrop-blur-sm"
            initial={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            {/* Decorative elements */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
              <motion.div
                animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                className="absolute -top-4 -left-4 h-8 w-8 rounded-full bg-purple-500/20"
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
                className="absolute -right-4 -bottom-4 h-6 w-6 rounded-full bg-violet-500/20"
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              />
            </div>
            <div className="relative z-10 flex items-center justify-center gap-3">
              <Icon className="h-5 w-5 text-green-500" icon="tabler:mail-check" />
              <span className="text-accent-foreground font-medium">
                {t(
                  "onboarding:invite.emailSentToStudents",
                  "Invitations sent to {{count}} students",
                  { count: selectedStudents.length },
                )}
              </span>
            </div>
          </motion.div>

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-3 sm:flex-row"
            initial={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Button
              className="w-full px-6 sm:w-auto sm:flex-1 sm:px-8"
              size="lg"
              variant="foreground"
              onClick={handleComplete}
            >
              {t("onboarding:invite.continue", "Continue to Dashboard")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative w-full space-y-6 sm:space-y-8">
      {/* Background circles */}
      <motion.div
        layout
        animate={{ opacity: 0.2 }}
        className="absolute -bottom-50 -left-50 size-120 rounded-full bg-gradient-to-br from-indigo-500/20 to-red-500/20 opacity-30 transition-colors duration-700"
        initial={{ opacity: 0 }}
        layoutId="circle1"
        transition={{
          default: { duration: 0.75 },
          layout: { duration: 0.75 },
        }}
      />
      <motion.div
        layout
        animate={{ opacity: 0.2 }}
        className="absolute -top-50 -right-50 size-120 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 opacity-30 transition-colors duration-700"
        initial={{ opacity: 0.2 }}
        layoutId="circle2"
        transition={{
          layout: { duration: 0.75 },
        }}
      />

      {/* Header */}
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="relative z-50"
        initial={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <div className="mb-6 flex items-center gap-4">
          <Button size="sm" variant="ghost" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("common:back")}
          </Button>
        </div>

        <div className="space-y-4 text-center sm:space-y-6">
          <h2 className="text-accent-foreground text-2xl font-bold sm:text-3xl">
            {selectedType === "link"
              ? t("onboarding:invite.form.link.title", "One-click join link")
              : t("onboarding:invite.form.email.title", "Send personal email invites")}
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg">
            {selectedType === "link"
              ? t(
                  "onboarding:invite.form.link.description",
                  "Generate a link your students can click to instantly join. Share it in chat, email, or anywhere.",
                )
              : t(
                  "onboarding:invite.form.email.description",
                  "Search for students and deliver a tailored invite straight to their inbox.",
                )}
          </p>
        </div>
      </motion.div>

      {/* Content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        {/* Form Side */}
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div className="bg-foreground/50 rounded-2xl p-4 shadow-2xl backdrop-blur-xl sm:p-6">
            {/* Decorative elements */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
              <motion.div
                animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                className="absolute -top-8 -left-8 h-16 w-16 rounded-full bg-blue-500/20"
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
                className="absolute -right-6 -bottom-6 h-12 w-12 rounded-full bg-purple-500/20"
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              />
            </div>
            {selectedType === "link" ? (
              <Form {...linkForm}>
                <form
                  className="space-y-4"
                  onSubmit={linkForm.handleSubmit(handleLinkInviteSubmit)}
                >
                  <FormField
                    control={linkForm.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2 text-base">
                          <CheckCircle2 className="h-5 w-5" />
                          {t("workspaces:invitationType.label")}
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="h-12 text-base">
                              <SelectValue placeholder={t("workspaces:invitationType.label")} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="ONE_TIME">
                              {t("workspaces:invitationType.one_time")}
                            </SelectItem>
                            <SelectItem value="UNLIMITED">
                              {t("workspaces:invitationType.unlimited")}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={linkForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel className="flex items-center gap-2 text-base">
                          <Edit3 className="h-5 w-5" />
                          {t("workspaces:message")}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            className="min-h-20 resize-none text-base"
                            maxLength={500}
                            placeholder={t("workspaces:workspaceDescriptionPlaceholder")}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="flex justify-end">
                          <span
                            className={`font-mono ${field.value && field.value.length > 400 ? "text-amber-500" : ""}`}
                          >
                            {field?.value?.length ?? 0}/500
                          </span>
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    className="h-10 w-full text-base"
                    disabled={submitting}
                    size="lg"
                    type="submit"
                    variant="foreground"
                  >
                    {submitting ? t("common:loading") : t("workspaces:createInvitation")}
                  </Button>
                </form>
              </Form>
            ) : (
              <Form {...emailForm}>
                <form
                  className="space-y-3"
                  onSubmit={emailForm.handleSubmit(handleEmailInviteSubmit)}
                >
                  {/* Required Field: Email Address */}
                  <FormField
                    control={emailForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2 text-sm">
                          <Mail className="h-4 w-4" />
                          {t("workspaces:emailAddress")}
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              className="focus:ring-primary/20 h-10 pr-12 transition-all duration-200 focus:ring-2"
                              placeholder={t("workspaces:emailAddressPlaceholder")}
                              type="email"
                              {...field}
                              onBlur={handleEmailBlur}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleSearchClick();
                                }
                              }}
                            />
                            <Button
                              className="absolute top-1/2 right-1 h-8 w-8 -translate-y-1/2 p-0"
                              disabled={searching}
                              size="sm"
                              type="button"
                              variant="ghost"
                              onClick={handleSearchClick}
                            >
                              {searching ? (
                                <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                              ) : (
                                <Search className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </FormControl>
                        <FormDescription className="text-xs">
                          {t("workspaces:emailAddressHelp")}
                        </FormDescription>
                        <FormMessage />
                        {validationError && (
                          <motion.div
                            animate={{ opacity: 1, y: 0 }}
                            className="rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-950"
                            initial={{ opacity: 0, y: -10 }}
                          >
                            <p className="text-xs text-red-600 dark:text-red-400">
                              {validationError}
                            </p>
                          </motion.div>
                        )}
                      </FormItem>
                    )}
                  />

                  {/* Search Error */}
                  {searchError && (
                    <motion.div animate={{ opacity: 1, y: 0 }} initial={{ opacity: 0, y: -10 }}>
                      <p className="text-sm text-red-500">{searchError}</p>
                    </motion.div>
                  )}

                  {/* Optional Field: Message */}
                  <FormField
                    control={emailForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2 text-sm">
                          <MessageSquare className="h-4 w-4" />
                          {t("workspaces:message")}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            className="focus:ring-primary/20 min-h-20 resize-none transition-all duration-200 focus:ring-2"
                            maxLength={500}
                            placeholder={t("workspaces:workspaceDescriptionPlaceholder")}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="flex justify-end text-xs">
                          <span
                            className={`font-mono ${field.value && field.value.length > 400 ? "text-amber-500" : ""}`}
                          >
                            {field?.value?.length ?? 0}/500
                          </span>
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    className="h-10 w-full gap-2"
                    disabled={!canSubmit}
                    size="lg"
                    type="submit"
                    variant="foreground"
                  >
                    {submitting ? t("common:loading") : t("workspaces:sendEmailInvitation")}
                  </Button>
                </form>
              </Form>
            )}
          </div>
        </motion.div>

        {/* Info Side */}
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-1"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <div className="bg-foreground/50 relative overflow-hidden rounded-2xl p-4 shadow-2xl backdrop-blur-xl sm:p-6">
            {/* Decorative elements */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
              <motion.div
                animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                className="absolute -bottom-6 -left-6 h-12 w-12 rounded-full bg-green-500/20"
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
                className="absolute -top-4 -right-4 h-8 w-8 rounded-full bg-emerald-500/20"
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
              />
            </div>
            {selectedType === "email" ? (
              <div className="relative z-10 flex h-full flex-col">
                <h4 className="text-accent-foreground mb-4 text-lg font-semibold">
                  {t("workspaces:selectedStudents", "Selected Students")}
                </h4>
                <div className="flex h-full min-h-64 flex-col">
                  {selectedStudents.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center text-center">
                      <Icon
                        className="text-muted-foreground mb-2 h-6 w-6"
                        icon="tabler:users-off"
                      />
                      <p className="text-muted-foreground text-xs">
                        {t("workspaces:noStudentsSelected")}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {t("workspaces:searchStudentsAbove")}
                      </p>
                    </div>
                  ) : (
                    <ScrollArea className="flex-1">
                      <div className="space-y-2 pr-4">
                        {selectedStudents.map((student) => (
                          <motion.div
                            key={student.id}
                            animate={{ opacity: 1, scale: 1 }}
                            className="group bg-muted/50 relative cursor-pointer overflow-hidden rounded-lg p-3 backdrop-blur-sm transition-all duration-300"
                            initial={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                          >
                            <div className="absolute inset-0 top-1/2 -left-0 h-24 w-24 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 opacity-25">
                              <img
                                alt={student.name}
                                className="h-full w-full object-cover blur-2xl"
                                src={student.avatar_path || "/placeholder.svg?height=40&width=40"}
                              />
                            </div>
                            <div className="flex items-center gap-2.5">
                              <div className="bg-muted/30 flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-md">
                                <img
                                  alt={student.name}
                                  className="h-full w-full object-cover"
                                  src={student.avatar_path || "/placeholder.svg?height=40&width=40"}
                                />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="text-accent-foreground truncate text-xs font-medium">
                                  {student.name}
                                </h4>
                                <p className="text-muted-foreground text-[0.65rem]">
                                  {t("common:roles.student")}
                                </p>
                              </div>
                              <Button
                                className="h-6 w-6 p-0 hover:bg-red-500/10 hover:text-red-600"
                                size="sm"
                                variant="ghost"
                                onClick={() => removeStudentFromSelected(student.id)}
                              >
                                <Icon className="h-3 w-3" icon="tabler:x" />
                              </Button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              </div>
            ) : (
              <div className="relative z-10 flex h-full flex-col">
                <h4 className="text-accent-foreground mb-4 text-lg font-semibold">
                  {t("workspaces:invitationTips")}
                </h4>
                <ul className="text-muted-foreground flex-1 space-y-3 text-sm">
                  {selectedType === "link" ? (
                    <>
                      <motion.li
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-muted/30 flex items-start gap-3 rounded-lg p-3 backdrop-blur-sm"
                        initial={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.2, delay: 0.1 }}
                      >
                        <div className="flex items-center rounded-full bg-blue-500/20 p-1.5">
                          <Icon className="h-4 w-4 text-blue-500" icon="tabler:check" />
                        </div>
                        <span className="leading-relaxed">{t("workspaces:invitationTip1")}</span>
                      </motion.li>
                      <motion.li
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-muted/30 flex items-start gap-3 rounded-lg p-3 backdrop-blur-sm"
                        initial={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.2, delay: 0.2 }}
                      >
                        <div className="flex items-center rounded-full bg-blue-500/20 p-1.5">
                          <Icon className="h-4 w-4 text-blue-500" icon="tabler:check" />
                        </div>
                        <span className="leading-relaxed">{t("workspaces:invitationTip2")}</span>
                      </motion.li>
                    </>
                  ) : (
                    <>
                      <motion.li
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-muted/30 flex items-start gap-3 rounded-lg p-3 backdrop-blur-sm"
                        initial={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.2, delay: 0.1 }}
                      >
                        <div className="flex items-center rounded-full bg-purple-500/20 p-1.5">
                          <Icon className="h-4 w-4 text-purple-500" icon="tabler:check" />
                        </div>
                        <span className="leading-relaxed">{t("workspaces:emailInviteTip1")}</span>
                      </motion.li>
                      <motion.li
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-muted/30 flex items-start gap-3 rounded-lg p-3 backdrop-blur-sm"
                        initial={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.2, delay: 0.2 }}
                      >
                        <div className="flex items-center rounded-full bg-purple-500/20 p-1.5">
                          <Icon className="h-4 w-4 text-purple-500" icon="tabler:check" />
                        </div>
                        <span className="leading-relaxed">{t("workspaces:emailInviteTip2")}</span>
                      </motion.li>
                    </>
                  )}
                </ul>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
